import React from 'react'
import { PortableText, PortableTextBlock } from '@portabletext/react'

interface CalloutProps {
  value: {
    type: 'info' | 'warning' | 'error' | 'success' | 'note'
    heading?: string
    body: PortableTextBlock[]
  }
}

/**
 * Custom component for rendering callouts/admonitions in rich text content
 */
const Callout: React.FC<CalloutProps> = ({ value }) => {
  const { type = 'info', heading, body } = value

  // Define styles based on callout type
  const styles = {
    info: {
      container: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
      icon: 'text-blue-500 dark:text-blue-400',
      heading: 'text-blue-800 dark:text-blue-300',
    },
    warning: {
      container: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
      icon: 'text-yellow-500 dark:text-yellow-400',
      heading: 'text-yellow-800 dark:text-yellow-300',
    },
    error: {
      container: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
      icon: 'text-red-500 dark:text-red-400',
      heading: 'text-red-800 dark:text-red-300',
    },
    success: {
      container: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
      icon: 'text-green-500 dark:text-green-400',
      heading: 'text-green-800 dark:text-green-300',
    },
    note: {
      container: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
      icon: 'text-gray-500 dark:text-gray-400',
      heading: 'text-gray-800 dark:text-gray-300',
    },
  }

  // Icons for each callout type
  const icons = {
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    note: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v10H5V5z"
          clipRule="evenodd"
        />
      </svg>
    ),
  }

  // Default headings if not provided
  const defaultHeadings = {
    info: 'Information',
    warning: 'Warning',
    error: 'Error',
    success: 'Success',
    note: 'Note',
  }

  return (
    <div className={`my-6 p-4 border-l-4 rounded-r-md ${styles[type].container}`}>
      <div className="flex items-start">
        <div className={`mr-3 mt-1 ${styles[type].icon}`}>{icons[type]}</div>
        <div className="flex-1">
          {(heading || defaultHeadings[type]) && (
            <h4 className={`text-base font-medium mb-2 ${styles[type].heading}`}>
              {heading || defaultHeadings[type]}
            </h4>
          )}
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <PortableText value={body} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Callout
